import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Sparkles, Lock, Zap, Shield, Hammer, Check, Download, Share2, X, Link as LinkIcon, Loader2 } from 'lucide-react';
import { ENGINES, ARC_DATA } from '../mock/mock';
import { assertBackendUrl } from '../lib/backendUrl';

// Fibonacci 55/34/11 logic and per-engine explanations (faithful to original index.html)
const FIBONACCI = {
  genius:    { pct:'55%', label:'The Profit Engine',    hours:'22 hrs/week', mandate:'Spend 22 hours/week here. This is where you create the most value and drive actual growth.', risk:'Ignore this and you will spend all your time working but never actually get ahead.' },
  execution: { pct:'34%', label:'The Infrastructure',   hours:'13 hrs/week', mandate:'Spend 13 hours/week here. This is the core system that keeps your daily operations running smoothly.', risk:'Ignore this and everything will slowly fall apart as you try to grow.' },
  shadow:    { pct:'11%', label:'The Emergency Brake',  hours:'5 hrs/week',  mandate:'Spend 5 hours/week here. This is your strict, dedicated time to step back, rest, and think.', risk:'Ignore this and you will burn out and crash the whole machine.' },
};

const ENGINE_EXPLAIN = {
  TITAN:     { genius:'This is your engine of conquest. 55% of your working hours go here because scale, resource acquisition, and forward momentum is where your psychology operates at full capacity. Cutting this short is how Titans stagnate.', execution:'Your operational infrastructure. Titan drive needs systems behind it or it collapses into chaos. This engine keeps your machine running while you push forward.', shadow:'Your recovery and pattern check. Pure rest and strategic pause. Without it, the Titan burns out and makes reckless decisions that cost years.' },
  ARBITER:   { genius:'This is your engine of order. 55% of your working hours go here because your ability to build systems, enforce standards, and design structures that do not break is your highest commercial value.', execution:'Your execution engine. Arbiter precision needs a force to apply it. This engine handles the movement and momentum your standards require to produce outcomes.', shadow:'Your reset window. Arbiters who skip this become rigid rather than structured. This time recalibrates judgment and prevents rules from becoming cages.' },
  ALCHEMIST: { genius:'This is your engine of architecture. 55% of your working hours go here because your ability to design, build, and engineer complex systems is where you create value others cannot replicate.', execution:'Your operational backbone. Alchemist systems need reliable infrastructure to run on. This engine handles the consistent day-to-day that keeps your builds functional.', shadow:'Your thinking time. Alchemists who skip this run on old models. This is when your architecture upgrades, not just executes.' },
  ARTISAN:   { genius:'This is your engine of creation. 55% of your working hours go here because your ability to produce work that resonates emotionally is where your commercial value lives. Output that moves people is the Artisan moat.', execution:'Your structural support. Artisan output needs systems behind it or the creative work remains inconsistent and unscalable. This engine is the frame the art hangs on.', shadow:'Your refill window. Artisans who skip this produce derivative work. The shadow time is where the original vision regenerates.' },
  ORACLE:    { genius:'This is your engine of pattern recognition. 55% of your working hours go here because your ability to read signals others miss and predict trajectory before it is visible is your rarest commercial asset.', execution:'Your delivery engine. Oracle insight without execution infrastructure is just prediction. This engine converts what you see into something the market can use and pay for.', shadow:'Your data intake window. Oracles who skip this become echo chambers of their old models. This time exposes you to new signal sources that keep your pattern library current.' },
  SOVEREIGN: { genius:'This is your engine of independence. 55% of your working hours go here because your ability to operate without external dependency, build systems that answer to no one, and create autonomous income is your primary commercial architecture.', execution:'Your operational discipline. Sovereign freedom needs a structure beneath it or it drifts into avoidance. This engine handles the consistent compounding actions that build the independence you target.', shadow:'Your detachment window. Sovereigns who skip this mistake isolation for independence. This time maintains the perspective needed to ensure you are building freedom, not just avoiding obligation.' },
};

const CONFLICTS = { SOVEREIGN:'ARBITER', ARBITER:'SOVEREIGN', TITAN:'ARTISAN', ARTISAN:'TITAN', ALCHEMIST:'ORACLE', ORACLE:'ALCHEMIST' };

function PracticalNote({ title, children }) {
  return (
    <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(199,164,109,0.055)', border: '1px solid rgba(199,164,109,0.18)' }}>
      <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: '#C7A46D' }}>{title}</div>
      <p className="text-[13.5px] text-white/72 leading-[1.7] m-0">{children}</p>
    </div>
  );
}

// Fractal signature SVG
function FractalSignature({ primary, secondary, tertiary }) {
  return (
    <div className="relative w-full aspect-square max-w-[460px] mx-auto">
      <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
          <filter id="frac-glow"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <circle cx="200" cy="200" r="180" fill="none" stroke="#333" strokeWidth="1" opacity="0.3"/>
        <circle cx="200" cy="200" r="140" fill="none" stroke="#333" strokeWidth="1" opacity="0.3"/>
        <circle cx="200" cy="200" r="100" fill="none" stroke="#333" strokeWidth="1" opacity="0.3"/>
        <circle cx="200" cy="200" r="60"  fill="none" stroke="#333" strokeWidth="1" opacity="0.3"/>
        <path className="golden-spiral" d="M200 200 L200 100 A100 100 0 0 1 300 200" fill="none" stroke={primary?.primary} strokeWidth="4" strokeLinecap="round" filter="url(#frac-glow)"/>
        {secondary && <path className="golden-spiral" d="M300 200 L200 200 L200 300 A100 100 0 0 0 100 200" fill="none" stroke={secondary?.primary} strokeWidth="4" strokeLinecap="round" filter="url(#frac-glow)" style={{ animationDelay: '0.4s' }}/>}
        {tertiary  && <path className="golden-spiral" d="M100 200 L200 200 L200 150 A50 50 0 0 1 250 200"   fill="none" stroke={tertiary?.primary}  strokeWidth="4" strokeLinecap="round" filter="url(#frac-glow)" style={{ animationDelay: '0.8s' }}/>}
        <circle cx="200" cy="200" r="8" fill={primary?.primary} filter="url(#frac-glow)" className="pulse-soft"/>
      </svg>
      <div className="absolute top-2 right-2 text-right">
        <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: primary?.primary }}>55% Genius</div>
        <div className="font-data text-[9px] tracking-[0.22em] uppercase text-white/45 mt-0.5">Primary Engine</div>
      </div>
      {secondary && (
        <div className="absolute bottom-2 left-2">
          <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: secondary?.primary }}>34% Execution</div>
          <div className="font-data text-[9px] tracking-[0.22em] uppercase text-white/45 mt-0.5">Secondary Engine</div>
        </div>
      )}
      {tertiary && (
        <div className="absolute top-2 left-2">
          <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: tertiary?.primary }}>11% Shadow</div>
          <div className="font-data text-[9px] tracking-[0.22em] uppercase text-white/45 mt-0.5">Tertiary Engine</div>
        </div>
      )}
    </div>
  );
}

function ArcEdgeCard({ arc, primary }) {
  return (
    <div className="relative panel-strong p-7 rounded-2xl overflow-hidden" style={{ borderColor: `${primary?.primary}50` }}>
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${primary?.primary}26, transparent 70%)` }} />
      <div className="relative flex items-center gap-4 mb-6">
        <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: `${primary?.primary}26`, border: `1px solid ${primary?.primary}50` }}>
          <Sparkles size={18} style={{ color: primary?.primary }} />
        </div>
        <div>
          <div className="font-head text-[18px] text-white">{arc?.tagline}</div>
          <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45 mt-0.5">Your Unfair Advantage</div>
        </div>
      </div>
      <div className="relative rounded-xl p-5 mb-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <p className="text-[14px] text-white/85 leading-[1.65] italic">"{arc?.edge}"</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[['1%', 'Advantage', primary?.primary], ['10x', 'Leverage', '#fff'], ['∞', 'Scale', '#fff']].map(([n, l, c]) => (
          <div key={l} className="text-center rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="font-head text-[24px] font-bold" style={{ color: c }}>{n}</div>
            <div className="font-data text-[9px] tracking-[0.22em] uppercase text-white/45 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhaseShiftCard({ primary, secondary }) {
  if (!secondary) return null;
  const isConflict = CONFLICTS[primary?.key] === secondary?.key;
  if (isConflict) {
    return (
      <div className="panel-strong p-6 rounded-2xl" style={{ borderColor: 'rgba(199,164,109,0.35)', borderLeftWidth: 4, borderLeftColor: '#C7A46D' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(199,164,109,0.12)' }}>
            <Zap size={16} style={{ color: '#C7A46D' }} />
          </div>
          <div>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: '#C7A46D' }}>Phase Shift Detected</div>
            <div className="text-white/70 text-[12px] mt-0.5">Your top Engines may pull you in different directions.</div>
          </div>
        </div>
        <p className="pt-4 mt-2 border-t border-white/8 text-[13.5px] text-white/80 leading-[1.7]">
          <span className="font-bold" style={{ color: '#C7A46D' }}>Note:</span> You are balancing the drive of {primary?.name} with the methodology of {secondary?.name}. Manage this friction carefully to scale effectively.
        </p>
      </div>
    );
  }
  return (
    <div className="panel-strong p-6 rounded-2xl" style={{ borderColor: 'rgba(29,158,117,0.35)', borderLeftWidth: 4, borderLeftColor: '#1D9E75' }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(29,158,117,0.12)' }}>
          <Check size={16} style={{ color: '#1D9E75' }} />
        </div>
          <div>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: '#1D9E75' }}>High Integrity Signature</div>
            <div className="text-white/70 text-[12px] mt-0.5">Your vision and execution habits are pulling in the same direction.</div>
        </div>
      </div>
      <p className="pt-4 mt-2 border-t border-white/8 text-[13.5px] text-white/80 leading-[1.7]">
        <span className="font-bold" style={{ color: '#1D9E75' }}>Excellent:</span> Strong alignment between your responses. Your strategic vision matches your execution habits.
      </p>
    </div>
  );
}

function EngineVectorMap({ result, isFull }) {
  const max = Math.max(...Object.values(result.scores));
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {Object.entries(result.scores).map(([key, score]) => {
        const e = ENGINES[key];
        const pct = max > 0 ? Math.round((score / max) * 100) : 0;
        const isPrimary = key === result.top;
        const reveal = isFull || isPrimary;
        if (!reveal) {
          return (
            <div key={key} className="relative rounded-xl p-5 overflow-hidden min-h-[140px]" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="opacity-15 blur-[3px] pointer-events-none select-none">
                <div className="h-12 w-12 rounded-full mx-auto mb-3 bg-[#222]" />
                <div className="font-data text-[10px] tracking-[0.22em] uppercase text-center text-[#333]">XXXX</div>
                <div className="font-head text-[24px] text-center text-[#2a2a2a] mt-1">??%</div>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#050403]/80 backdrop-blur-[2px]">
                <Lock size={14} className="text-white/40 mb-1.5" />
                <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45 font-bold">Locked</div>
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="text-center rounded-xl p-5 transition-transform hover:-translate-y-0.5"
               style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderBottom: isPrimary ? `2px solid ${e.primary}` : '1px solid rgba(255,255,255,0.06)' }}>
            <div className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: `${e.primary}26` }}>
              <div className="h-6 w-6 rounded-full" style={{ background: e.primary }} />
            </div>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: e.primary }}>{e.name}</div>
            <div className="font-head text-[26px] font-bold text-white">{pct}%</div>
            <div className="font-data text-[9px] tracking-[0.22em] uppercase text-white/45 mt-1.5">{isPrimary ? 'Primary' : (e.description?.split('&')[0]?.trim() || '')}</div>
          </div>
        );
      })}
    </div>
  );
}

function FibonacciTable({ primary, secondary, tertiary }) {
  const rows = [
    { kind:'genius',    color: primary?.primary,    engineName: primary?.name,    explanation: ENGINE_EXPLAIN[primary?.key]?.genius,    icon: <Zap size={20} className="shrink-0" /> },
    { kind:'execution', color: secondary?.primary,  engineName: secondary?.name,  explanation: ENGINE_EXPLAIN[secondary?.key]?.execution, icon: <Hammer size={20} className="shrink-0" /> },
    { kind:'shadow',    color: tertiary?.primary,   engineName: tertiary?.name,   explanation: ENGINE_EXPLAIN[tertiary?.key]?.shadow,   icon: <Shield size={20} className="shrink-0" /> },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-head text-[20px] text-white">Fibonacci Allocation <span className="text-white/40">(55/34/11)</span></h3>
        <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45 mt-1">A practical split for where your attention should go first.</div>
      </div>
      <PracticalNote title="What this means">
        This is not decoration. It turns your ARC signal into a weekly operating guide: spend most time where your Engine creates value, protect the systems that keep you consistent, and reserve time to recover and think.
      </PracticalNote>
      {rows.map((row) => {
        const f = FIBONACCI[row.kind];
        return (
          <div key={row.kind} className="relative rounded-xl p-6 overflow-hidden"
               style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: `3px solid ${row.color || '#C7A46D'}` }}>
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none opacity-10" style={{ background: `radial-gradient(circle, ${row.color || '#C7A46D'}, transparent 70%)` }} />
            <div className="relative flex items-center gap-4 mb-5">
              <span style={{ color: row.color || '#C7A46D' }}>{row.icon}</span>
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="font-head text-[28px] font-bold" style={{ color: row.color || '#C7A46D' }}>{f.pct}</span>
                  <span className="font-data text-[10px] tracking-[0.22em] uppercase font-bold text-white/70">{f.label}</span>
                </div>
                <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45 mt-1">{row.engineName} - <span className="text-white/85">{f.hours}</span></div>
              </div>
            </div>
            {row.explanation && (
              <div className="relative rounded-lg p-4 mb-4" style={{ background: 'rgba(0,0,0,0.25)', borderLeft: `4px solid ${row.color || '#C7A46D'}40` }}>
                <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: row.color || '#C7A46D' }}>Why this engine, at this allocation</div>
                <p className="text-[13.5px] text-white/85 leading-[1.7]">{row.explanation}</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-lg p-4" style={{ background: 'rgba(29,158,117,0.06)', border: '1px solid rgba(29,158,117,0.18)' }}>
                <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: '#1D9E75' }}>The Mandate</div>
                <p className="text-[13px] text-white/85 leading-[1.7]">{f.mandate}</p>
              </div>
              <div className="rounded-lg p-4" style={{ background: 'rgba(224,82,82,0.06)', border: '1px solid rgba(224,82,82,0.18)' }}>
                <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: '#e05252' }}>The Risk</div>
                <p className="text-[13px] text-white/85 leading-[1.7]">{f.risk}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LockedSection({ title, children, blurMessage = 'Complete all 60 questions to unlock', onContinue, ctaLabel = 'Continue Assessment' }) {
  return (
    <div className="relative rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="p-7 blur-[6px] select-none pointer-events-none">
        <h3 className="font-head text-[18px] text-white mb-4">{title}</h3>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-[#050403]/85 backdrop-blur-[2px]">
        <div className="text-center px-6">
          <Lock size={28} className="mx-auto mb-3 text-white/55" />
          <div className="font-data text-[11px] tracking-[0.22em] uppercase font-bold text-white">{title} Locked</div>
          <div className="text-white/55 text-[12px] mt-2">{blurMessage}</div>
          {onContinue && (
            <button onClick={onContinue} className="constellation-cta mt-5">
              {ctaLabel} <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CareerFounderPath({ arc }) {
  return (
    <div>
      <h3 className="font-head text-[20px] text-white mb-6">Career &amp; Founder Path</h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#C7A46D' }}>Career Lane</div>
          <div className="space-y-3">
            {(arc?.career || []).map((c, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="font-bold text-white text-[13px] tracking-[0.18em] uppercase">{c.title}</div>
                <div className="font-data text-[12px] mt-2" style={{ color: '#C7A46D' }}>{c.salary}</div>
                <div className="text-white/65 text-[13.5px] mt-3 leading-[1.65]">{c.description}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: '#1D9E75' }}>Founder Lane</div>
          <div className="space-y-3">
            {(arc?.founder || []).map((f, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="font-bold text-white text-[13px] tracking-[0.18em] uppercase">{f.title}</div>
                <div className="font-data text-[12px] mt-2" style={{ color: '#1D9E75' }}>{f.valuation}</div>
                <div className="text-white/65 text-[13.5px] mt-3 leading-[1.65]">{f.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Share Arc card (Canvas-rendered PNG)
function drawShareCard(canvas, { result, primary, secondary, tertiary, arc }) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  // bg
  ctx.fillStyle = '#050403';
  ctx.fillRect(0, 0, W, H);
  // gradient corners
  const g1 = ctx.createRadialGradient(W * 0.85, H * 0.1, 50, W * 0.85, H * 0.1, 700);
  g1.addColorStop(0, primary.primary + 'AA');
  g1.addColorStop(1, '#05040300');
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  // gold border
  ctx.strokeStyle = '#C7A46D'; ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  // small mark
  ctx.fillStyle = '#C7A46D';
  ctx.font = 'bold 18px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('ARCLIPSE CONSTELLATION', 60, 70);
  // engine eyebrow
  ctx.fillStyle = primary.primary;
  ctx.font = 'bold 22px Inter, sans-serif';
  ctx.fillText(primary.name + ' ENGINE', 60, 170);
  // arc title (huge)
  ctx.fillStyle = primary.primary;
  ctx.font = 'bold 110px Cormorant Garamond, serif';
  ctx.fillText('The ' + result.arcKey, 60, 290);
  // tagline
  ctx.fillStyle = '#ffffff';
  ctx.font = '300 30px Inter, sans-serif';
  ctx.fillText(arc?.tagline || '', 60, 340);
  // accuracy chip
  ctx.fillStyle = '#C7A46D';
  ctx.font = 'bold 14px Inter, sans-serif';
  ctx.fillText('- ' + (result.accuracy >= 94 ? 'FULL ARC SIGNAL - 94% ACCURACY' : 'PARTIAL - ' + result.accuracy + '% ACCURACY'), 60, 380);
  // engine vector mini bars (only primary if not full)
  const isFull = result.accuracy >= 94;
  const max = Math.max(...Object.values(result.scores));
  const barX = 60, barY = 440, barW = (W - 120);
  const order = ['TITAN', 'ARBITER', 'ALCHEMIST', 'ARTISAN', 'ORACLE', 'SOVEREIGN'];
  order.forEach((k, i) => {
    const sc = result.scores[k] || 0;
    const pct = max > 0 ? sc / max : 0;
    const e = ENGINES[k];
    const isPrim = k === result.top;
    const y = barY + i * 24;
    // label
    ctx.fillStyle = (isFull || isPrim) ? e.primary : '#444';
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(k, barX, y + 12);
    // bar bg
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(barX + 110, y, barW - 110 - 60, 12);
    // bar fill
    if (isFull || isPrim) {
      ctx.fillStyle = e.primary;
      ctx.fillRect(barX + 110, y, (barW - 110 - 60) * pct, 12);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 12px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(Math.round(pct * 100) + '%', W - 60, y + 12);
    } else {
      // locked
      ctx.fillStyle = '#333';
      ctx.fillRect(barX + 110, y, (barW - 110 - 60) * 0.4, 12);
      ctx.fillStyle = '#444';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('LOCKED', W - 60, y + 12);
    }
  });
  // footer
  ctx.fillStyle = '#666';
  ctx.font = '13px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Find your Arc at constellation.arclipse.com', 60, H - 60);
  // arc spiral mini glyph (top-right corner)
  ctx.save();
  ctx.translate(W - 220, 200);
  ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.strokeStyle = primary.primary;
  ctx.beginPath(); ctx.arc(0, 0, 90, Math.PI, Math.PI * 1.5); ctx.stroke();
  if (secondary) {
    ctx.strokeStyle = secondary.primary;
    ctx.beginPath(); ctx.arc(0, 0, 60, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
  }
  if (tertiary) {
    ctx.strokeStyle = tertiary.primary;
    ctx.beginPath(); ctx.arc(0, 0, 30, 0, Math.PI * 0.5); ctx.stroke();
  }
  ctx.fillStyle = primary.primary;
  ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

function ShareModal({ open, onClose, result, primary, secondary, tertiary, arc }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    drawShareCard(canvasRef.current, { result, primary, secondary, tertiary, arc });
  }, [open, result, primary, secondary, tertiary, arc]);

  const ensureLink = async () => {
    if (shareLink) return shareLink;
    setLinkBusy(true);
    try {
      const API = assertBackendUrl();
      const r = await fetch(`${API}/api/share/arc`, { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error('mint failed');
      const data = await r.json();
      const url = `${window.location.origin}/a/${data.token}`;
      setShareLink(url);
      return url;
    } catch {
      window.alert('Could not create a public link. Try again in a moment.');
      return '';
    } finally {
      setLinkBusy(false);
    }
  };

  const copyLink = async () => {
    const url = await ensureLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1400);
    } catch {}
  };

  const download = () => {
    const c = canvasRef.current;
    if (!c) return;
    const link = document.createElement('a');
    link.download = `ARCLIPSE_${result.arcKey}.png`;
    link.href = c.toDataURL('image/png');
    link.click();
  };

  const shareNative = async () => {
    const c = canvasRef.current;
    if (!c) return;
    setBusy(true);
    try {
      const url = await ensureLink();
      c.toBlob(async (blob) => {
        if (!blob) { setBusy(false); return; }
        const file = new File([blob], `ARCLIPSE_${result.arcKey}.png`, { type: 'image/png' });
        const text = `I just got my Arc: The ${result.arcKey}. ${arc?.tagline}.`;
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `The ${result.arcKey}`, text, url });
        } else if (navigator.share) {
          await navigator.share({ title: `The ${result.arcKey}`, text, url: url || window.location.origin });
        } else if (url) {
          await navigator.clipboard.writeText(`${text} ${url}`);
          window.alert('Share text + link copied to clipboard.');
        }
        setBusy(false);
      }, 'image/png');
    } catch { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#050403]/85 backdrop-blur-sm" onClick={onClose} data-testid="share-arc-modal">
      <div className="max-w-[920px] w-full panel-strong p-5 md:p-6 rounded-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45">Share your Arc</div>
            <div className="font-head text-[18px] text-white">A card you can post anywhere, plus a public link.</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost px-2 py-2" data-testid="share-arc-close"><X size={16}/></button>
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <canvas ref={canvasRef} width={1200} height={720} className="w-full h-auto block" />
        </div>

        {shareLink && (
          <div className="mt-4 px-3 py-2.5 rounded-lg flex items-center gap-2 font-data text-[12px]"
               style={{ background: 'rgba(199,164,109,0.06)', border: '1px solid rgba(199,164,109,0.3)' }}>
            <LinkIcon size={13} style={{ color: '#C7A46D' }} />
            <span className="text-white/85 truncate flex-1" data-testid="share-arc-public-link">{shareLink}</span>
            <button onClick={copyLink} className="btn btn-ghost px-3 py-1 text-[11px]" data-testid="share-arc-copy-shown">
              {linkCopied ? <><Check size={12}/> Copied</> : <>Copy</>}
            </button>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end">
          <button onClick={copyLink} className="btn" disabled={linkBusy} data-testid="share-arc-copylink">
            {linkBusy ? <><Loader2 size={13} className="animate-spin"/> Minting...</>
              : linkCopied ? <><Check size={13}/> Link copied</>
              : <><LinkIcon size={13}/> Copy public link</>}
          </button>
          <button onClick={download} className="btn" data-testid="share-arc-download"><Download size={14}/> Download PNG</button>
          <button onClick={shareNative} className="btn btn-primary" data-testid="share-arc-native" disabled={busy}><Share2 size={14}/> {busy ? 'Sharing...' : 'Share'}</button>
        </div>
      </div>
    </div>
  );
}

// Main exported component
export default function ArcResults({ result, onContinueDeeper, onEnter, enterLabel = 'Apply to Constellation', readOnly = false, ownerName = null }) {
  const [shareOpen, setShareOpen] = useState(false);
  const primary = ENGINES[result.top];
  const secondary = ENGINES[result.second];
  const tertiary = ENGINES[result.third];
  const arc = ARC_DATA[result.arcKey];
  const isFull = result.isFull || result.accuracy >= 94;

  return (
    <div className="max-w-[1180px] mx-auto fade-up pt-2 pb-16">
      {/* Hero */}
      <header className="text-center relative mb-10 md:mb-14">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full pointer-events-none opacity-10 blur-3xl"
             style={{ background: primary?.primary }} />
        <div className="relative">
          <div className="font-data text-[11px] tracking-[0.32em] uppercase text-white/45 mb-3">{primary?.name} Engine</div>
          <h1 className="font-logo text-[clamp(48px,9vw,108px)] leading-[1.0] font-black" style={{ color: primary?.primary }} data-testid="arc-result-title">
            The {result.arcKey}
          </h1>
          <div className="mt-4 font-head text-[20px] md:text-[26px] text-white/85">{arc?.tagline}</div>
          <p className="mt-4 max-w-[640px] mx-auto text-white/70 text-[14.5px] leading-[1.85] py-5 border-t border-b border-white/8">
            {arc?.description}
          </p>
          <p className="mt-4 max-w-[640px] mx-auto text-white/58 text-[14px] leading-[1.75]">
            Your Engine shows the force you lead with. Your Arc combines your strongest two Engines into a practical read on how you build, decide, and respond under pressure.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-bold"
               style={{ background: isFull ? 'rgba(29,158,117,0.10)' : 'rgba(199,164,109,0.10)', border: `1px solid ${isFull ? 'rgba(29,158,117,0.40)' : 'rgba(199,164,109,0.40)'}`, color: isFull ? '#1D9E75' : '#C7A46D' }}>
            {isFull ? <><Check size={12}/> Full ARC signal - 94% accuracy</> : <><Sparkles size={12}/> Partial - {result.accuracy}% accuracy</>}
          </div>
          <div className="mt-7 flex flex-col md:flex-row gap-3 justify-center">
            {!readOnly && onEnter && (
              <button
                onClick={onEnter}
                className="constellation-cta"
                data-testid="arc-enter-btn"
              >
                {enterLabel} <ArrowRight size={14}/>
              </button>
            )}
            {!readOnly && (
              <button onClick={() => setShareOpen(true)} className="btn px-7 py-3.5" data-testid="arc-share-btn">
                <Share2 size={14}/> Share your Arc
              </button>
            )}
            {readOnly && ownerName && (
              <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45 self-center">Owned by {ownerName}</div>
            )}
          </div>
        </div>
      </header>

      {/* Fractal signature + Arc Edge + Phase Shift */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="panel-strong p-6 md:p-8 rounded-2xl">
          <h3 className="font-head text-[18px] text-white text-center mb-2">Fractal Signature</h3>
          <p className="text-center text-white/55 text-[13px] leading-[1.6] mb-4">A visual map of your primary, secondary, and shadow Engine pattern.</p>
          <FractalSignature primary={primary} secondary={isFull ? secondary : null} tertiary={isFull ? tertiary : null} />
        </div>
        <div className="space-y-4">
          <ArcEdgeCard arc={arc} primary={primary} />
          {isFull ? <PhaseShiftCard primary={primary} secondary={secondary} /> : (
            <div className="panel-strong p-5 rounded-2xl text-center">
              <Lock size={22} className="mx-auto text-white/45 mb-2" />
              <div className="font-data text-[10px] tracking-[0.22em] uppercase font-bold text-white">Phase Shift Analysis Locked</div>
              <div className="text-white/55 text-[12px] mt-1">Reveals coherence between primary and secondary engines</div>
            </div>
          )}
        </div>
      </div>

      {/* Engine vector map */}
      <div className="panel-strong p-6 md:p-8 rounded-2xl mb-8">
        <h3 className="font-head text-[20px] text-white text-center mb-2">Engine Map</h3>
        <p className="text-center text-white/55 text-[13px] leading-[1.6] mb-5">Each bar shows how strongly your answers pointed toward that Engine.</p>
        {!isFull && <p className="text-center font-data text-[10px] tracking-[0.22em] uppercase text-white/45 mb-5">Primary Engine revealed. Complete the full ARC signal to unlock all six Engines.</p>}
        <EngineVectorMap result={result} isFull={isFull} />
      </div>

      {/* Secondary Engine */}
      {isFull ? (
        <div className="panel-strong p-6 md:p-8 rounded-2xl mb-8">
          <h3 className="font-head text-[18px] text-white mb-5">Secondary Engine</h3>
          <div className="flex items-center gap-4 rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.025)', borderLeft: `3px solid ${secondary?.primary}` }}>
            <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: `${secondary?.primary}30` }}>
              <div className="h-6 w-6 rounded-full" style={{ background: secondary?.primary }} />
            </div>
            <div>
              <div className="font-data text-[12px] tracking-[0.22em] uppercase font-bold" style={{ color: secondary?.primary }}>{secondary?.name}</div>
              <div className="text-white/65 text-[13.5px] mt-0.5">{secondary?.description}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <LockedSection title="Secondary Engine" blurMessage="This determines your specific Arc. Complete all 60 questions." onContinue={onContinueDeeper}>
            <div className="flex items-center gap-4 p-4 rounded-xl">
              <div className="h-10 w-10 rounded-full bg-gray-700" />
              <div>
                <div className="font-data text-[12px] tracking-[0.22em] uppercase font-bold text-gray-500">THE XXXXXXX</div>
                <div className="text-gray-600 text-[13px]">Xxxxxxxxxxx Xxxxxxxxxxx</div>
              </div>
            </div>
          </LockedSection>
        </div>
      )}

      {/* Fibonacci Allocation */}
      {isFull ? (
        <div className="panel-strong p-6 md:p-8 rounded-2xl mb-8">
          <FibonacciTable primary={primary} secondary={secondary} tertiary={tertiary} />
        </div>
      ) : (
        <div className="mb-8">
          <LockedSection title="Fibonacci Allocation" blurMessage="The 55/34/11 guide for focusing your weekly attention." onContinue={onContinueDeeper}>
            <div className="space-y-3">
              <div className="rounded-lg p-4 border-l-4 border-gray-700"><div className="font-bold text-gray-500">55% - The Profit Engine</div><div className="text-gray-600 text-[12px] mt-1">Spend XX hours/week here...</div></div>
              <div className="rounded-lg p-4 border-l-4 border-gray-800"><div className="font-bold text-gray-600">34% - The Infrastructure</div><div className="text-gray-700 text-[12px] mt-1">Spend XX hours/week here...</div></div>
            </div>
          </LockedSection>
        </div>
      )}

      {/* Career & Founder */}
      {isFull ? (
        <div className="panel-strong p-6 md:p-8 rounded-2xl mb-8">
          <CareerFounderPath arc={arc} />
        </div>
      ) : (
        <div className="mb-8">
          <LockedSection title="Career &amp; Founder Path" blurMessage="The exact lanes your Arc is built for." onContinue={onContinueDeeper}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl p-4 border border-white/10"><div className="font-bold text-gray-500">Chief XXXXXXX Officer</div><div className="font-data text-[12px] text-gray-600 mt-1">$XXX,000 to $X,XXX,000</div></div>
              <div className="rounded-xl p-4 border border-white/10"><div className="font-bold text-gray-500">XXXXXXX Startup</div><div className="font-data text-[12px] text-gray-600 mt-1">$XXM to $XXXM valuation</div></div>
            </div>
          </LockedSection>
        </div>
      )}

      <footer className="text-center font-data text-[10px] tracking-[0.22em] uppercase text-white/35">
        ARCLIPSE CONSTELLATION - Generated {new Date().toLocaleDateString()}
        <div className="mt-2 normal-case tracking-[0.06em]">This blueprint is based on your ARC signal. Reassess quarterly to track change over time.</div>
      </footer>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)}
                  result={result} primary={primary} secondary={secondary} tertiary={tertiary} arc={arc} />
    </div>
  );
}
